import {
  BarChart3,
  LifeBuoy,
  MessageCircle,
  Plus,
  Search,
  Ticket,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";

import { ADMIN_SECTIONS } from "../admin/sections";
import { can, useMe } from "../auth/session";
import { useChatLauncher } from "../chat/ChatDrawer";
import { useHotkeys } from "../lib/hotkeys";
import { useDebouncedValue } from "../lib/hooks";
import { useTickets } from "../tickets/api";
import { StatusBadge } from "../tickets/TicketBadges";
import { Dialog } from "./Dialog";
import "./palette.css";

/**
 * Keyboard shortcuts everywhere (DECISIONS #53): Ctrl+K (Cmd+K) opens a palette to jump to
 * any ticket or page, / goes to search, ? lists every shortcut.
 */
export function KeyboardShortcuts() {
  const navigate = useNavigate();
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  useHotkeys({
    "mod+k": () => setPalette(true),
    "?": () => setHelp(true),
    "/": () => {
      const box = document.getElementById("topbar-search");
      if (box && box.offsetParent !== null) box.focus();
      else navigate("/search");
    },
  });
  return (
    <>
      {palette ? <CommandPalette onClose={() => setPalette(false)} /> : null}
      {help ? <ShortcutHelp onClose={() => setHelp(false)} /> : null}
    </>
  );
}

interface Item {
  id: string;
  label: string;
  detail?: string;
  icon: LucideIcon;
  extra?: ReactNode;
  run: () => void;
}

const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD = MAC ? "⌘" : "Ctrl";

/** "123", "asp123" or "ASP-000123": the ticket reference it names. */
export function referenceIn(text: string): string | null {
  const match = /^(?:asp-?)?(\d{1,6})$/i.exec(text.trim());
  return match?.[1] ? `ASP-${match[1].padStart(6, "0")}` : null;
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const { openChat } = useChatLauncher();
  const id = useId();
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  // The dialog takes the focus as it opens; then hand it to the box.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => input.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, []);
  const term = useDebouncedValue(q.trim(), 150);
  const staff = can(me, "use_workspace");
  const tickets = useTickets({ scope: staff ? "workspace" : "mine", q: term || undefined, sort: "updated", limit: 5 });

  const items = useMemo(() => {
    const go = (to: string) => () => {
      onClose();
      navigate(to);
    };
    const pages: Item[] = [
      { id: "new", label: "Raise a ticket", icon: Plus, run: go("/tickets/new") },
      { id: "home", label: me.roles.includes("admin") ? "Home" : "Help", icon: LifeBuoy, run: go("/") },
      { id: "tickets", label: staff ? "Tickets" : "Your tickets", icon: Ticket, run: go("/tickets") },
      { id: "search", label: "Search tickets", icon: Search, run: go(term ? `/search?q=${encodeURIComponent(term)}` : "/search") },
      {
        id: "chat",
        label: staff ? "Chats" : "Chat with support",
        icon: MessageCircle,
        run: () => {
          onClose();
          openChat();
        },
      },
      ...(can(me, "view_reports") ? [{ id: "reports", label: "Reports", icon: BarChart3, run: go("/reports") }] : []),
      ...ADMIN_SECTIONS.flatMap(([, sections]) => sections)
        .filter((s) => can(me, s.permission))
        .map((s) => ({ id: s.to, label: s.label, detail: "Admin", icon: s.icon, run: go(s.to) })),
    ];
    const words = q.trim().toLowerCase();
    const matchingPages = words ? pages.filter((p) => p.label.toLowerCase().includes(words)) : pages.slice(0, 5);
    const reference = referenceIn(q);
    const found: Item[] = (tickets.data?.items ?? []).map((t) => ({
      id: t.reference,
      label: t.title,
      detail: t.reference,
      icon: Ticket,
      extra: <StatusBadge status={t.status} />,
      run: go(`/tickets/${t.reference}`),
    }));
    const direct: Item[] =
      reference && !found.some((t) => t.id === reference)
        ? [{ id: `open-${reference}`, label: `Open ${reference}`, icon: Ticket, run: go(`/tickets/${reference}`) }]
        : [];
    return [...direct, ...found, ...matchingPages];
  }, [me, staff, q, term, tickets.data, navigate, onClose, openChat]);

  const current = Math.min(index, Math.max(0, items.length - 1));

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setIndex((current + step + items.length) % Math.max(1, items.length));
    } else if (event.key === "Enter") {
      event.preventDefault();
      items[current]?.run();
    }
  }

  return (
    <Dialog open wide title="Go to" onClose={onClose} footer={<span className="palette-hint">Arrow keys to move, Enter to open, Esc to close</span>}>
      <div className="palette">
        <div className="search palette-search">
          <Search size={16} className="search-icon" aria-hidden="true" />
          <input
            ref={input}
            autoFocus
            className="input"
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-activedescendant={items[current] ? `${id}-${items[current].id}` : undefined}
            aria-label="Ticket number, title or page"
            placeholder="Ticket number, title or page"
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setIndex(0);
            }}
            onKeyDown={onKeyDown}
          />
        </div>
        <ul id={`${id}-list`} role="listbox" aria-label="Results" className="palette-list">
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <li
                key={item.id}
                id={`${id}-${item.id}`}
                role="option"
                aria-selected={i === current}
                className={i === current ? "palette-item palette-item-active" : "palette-item"}
                onMouseMove={() => setIndex(i)}
                onClick={item.run}
              >
                <Icon size={16} aria-hidden="true" className="palette-icon" />
                <span className="palette-label">{item.label}</span>
                {item.detail ? <span className="palette-detail">{item.detail}</span> : null}
                {item.extra}
              </li>
            );
          })}
          {items.length === 0 ? <li className="palette-empty">Nothing matches.</li> : null}
        </ul>
      </div>
    </Dialog>
  );
}

function ShortcutHelp({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const staff = can(me, "use_workspace");
  const groups: [string, [string[], string][]][] = [
    [
      "Anywhere",
      [
        [[MOD, "K"], "Go to a ticket or page"],
        [["/"], "Search tickets"],
        [["?"], "Show these shortcuts"],
      ],
    ],
    ...(staff
      ? ([
          [
            "Ticket lists",
            [
              [["J"], "Next ticket"],
              [["K"], "Previous ticket"],
              [["Enter"], "Open it beside the list"],
              [["Esc"], "Close it"],
            ],
          ],
        ] as [string, [string[], string][]][])
      : []),
    [
      "On a ticket",
      [
        [["R"], "Reply"],
        ...(staff
          ? ([
              [["N"], "Internal note"],
              [["E"], "Resolve"],
              [["A"], "Assign"],
            ] as [string[], string][])
          : []),
      ],
    ],
  ];
  return (
    <Dialog open title="Keyboard shortcuts" onClose={onClose} footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>}>
      <div className="shortcut-groups">
        {groups.map(([title, rows]) => (
          <section key={title} aria-label={title}>
            <h3 className="shortcut-title">{title}</h3>
            <dl className="shortcut-list">
              {rows.map(([keys, label]) => (
                <div key={label} className="shortcut-row">
                  <dt>{label}</dt>
                  <dd>
                    {keys.map((k) => (
                      <kbd key={k}>{k}</kbd>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Dialog>
  );
}
